import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCServiceRequestGroupApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request-group.api-model';
import {CpmCServiceRequestGroupApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request-group.api-service';
import {
    CpmCServiceRequestGroupCreateApiSchema,
    CpmCServiceRequestGroupUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-service-request-group.api-schemas';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

export class NewEditServiceRequestGroupModalConfig {
    constructor(public existing?: CpmCServiceRequestGroupApiModel) {}
}

export class NewEditServiceRequestGroupModalResult {
    constructor(public item?: CpmCServiceRequestGroupApiModel) {}
}

/**
 * New/Edit ServiceRequestGroup modal
 */
@Component({
    selector: 'app-new-edit-service-request-group-modal',
    templateUrl: './new-edit-service-request-group-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditServiceRequestGroupModalComponent extends IBaseCRUModalComponent<
    CpmCServiceRequestGroupApiModel,
    NewEditServiceRequestGroupModalComponent,
    NewEditServiceRequestGroupModalConfig,
    NewEditServiceRequestGroupModalResult
> {
    protected item?: CpmCServiceRequestGroupApiModel;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure(
            'ServiceRequestGroupApiModel',
            (model: CpmCServiceRequestGroupApiModel) => model.name ?? '<Unknown service request group>'
        );
        this.config.width = '30rem';
        this.contentMinHeight = '28rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, [])
        });

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading the service request group...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;

                    this.item = await firstValueFrom(
                        CpmCServiceRequestGroupApiService.getServiceRequestGroupByUUID(
                            this.config.data?.extended?.existing?.uuid ?? ''
                        )
                    );
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the service request group...';
            this.form.patchValue(this.item!);
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCServiceRequestGroupCreateApiSchema | CpmCServiceRequestGroupUpdateApiSchema = this
            .isCreationMode
            ? {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value
              }
            : {
                  name: this.form.controls['name'].value,
                  description: this.form.controls['description'].value
              };

        await this.submitCU(
            null,
            CpmCServiceRequestGroupApiService.createServiceRequestGroup,
            CpmCServiceRequestGroupApiService.updateServiceRequestGroup,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCServiceRequestGroupApiModel) => new NewEditServiceRequestGroupModalResult(resultCRUD)
        );
    }
}
